export interface ValidationError {
  type: "invalid" | "reactivate" | "format";
  emails?: string[];
  message: string;
}

export interface CategorizedEmails {
  valid: string[];
  invalid: string[];
  reactivate: string[];
}

interface EmailCategorizationParams {
  lowerEmail: string;
  seenEmails: Set<string>;
  existingEmails: { email: string; is_deleted: boolean }[];
}

const EMAIL_REGEX = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const parseEmailsFromText = (text: string): string[] => {
  return text
    .split(",")
    .map(email => email.trim())
    .filter(Boolean);
};

export const isValidEmailFormat = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

const categorizeEmail = ({
  lowerEmail,
  seenEmails,
  existingEmails
}: EmailCategorizationParams): keyof CategorizedEmails | "skip" => {
  if (!isValidEmailFormat(lowerEmail)) {
    return "invalid";
  }
  if (seenEmails.has(lowerEmail)) {
    return "skip";
  }

  const existingEmail = existingEmails.find(e => e.email.toLowerCase() === lowerEmail);
  if (!existingEmail) {
    return "valid";
  }
  if (existingEmail.is_deleted) {
    return "reactivate";
  }
  return "skip";
};

export const categorizeEmails = (
  emails: string[],
  existingEmails: { email: string; is_deleted: boolean }[]
): CategorizedEmails => {
  const seenEmails = new Set<string>();

  return emails.reduce((acc, email) => {
    const lowerEmail = email.toLowerCase();
    const category = categorizeEmail({
      lowerEmail,
      seenEmails,
      existingEmails
    });

    if (category === "skip") {
      return acc;
    }

    seenEmails.add(lowerEmail);

    if (category === "reactivate") {
      acc.reactivate.push(lowerEmail);
      acc.valid.push(lowerEmail);
    } else if (category === "valid") {
      acc.valid.push(lowerEmail);
    } else {
      acc[category].push(lowerEmail);
    }

    return acc;
  }, {
    valid: [],
    invalid: [],
    reactivate: []
  } as CategorizedEmails);
};

const createValidationError = (
  type: ValidationError["type"],
  emails: string[],
  messageTemplate: (emails: string[]) => string
): ValidationError | null => {
  if (emails.length === 0) {
    return null;
  }
  return { type, emails, message: messageTemplate(emails) };
};

const formatReactivateMessage = (emails: string[]): string => {
  const emailPlural = emails.length > 1 ? "s were" : " was";
  return `The following email${emailPlural} previously deleted and will be reactivated: ${emails.join(", ")}`;
};

const buildErrorMessages = (categorized: CategorizedEmails): ValidationError[] => {
  const errorTemplates: Array<{
    type: ValidationError["type"];
    emails: string[];
    template: (emails: string[]) => string;
  }> = [
    {
      type: "invalid",
      emails: categorized.invalid,
      template: emails => `Invalid email addresses: ${emails.join(", ")}`
    },
    {
      type: "reactivate",
      emails: categorized.reactivate,
      template: formatReactivateMessage
    }
  ];

  return errorTemplates
    .map(({ type, emails, template }) => createValidationError(type, emails, template))
    .filter((error): error is ValidationError => error !== null);
};

export const validateEmails = (
  text: string,
  existingEmails: { email: string; is_deleted: boolean }[]
): { valid: string[]; errors: ValidationError[] } => {
  const trimmedText = text.trim();

  if (!trimmedText) {
    return {
      valid: [],
      errors: [{ type: "format", message: "Please enter email addresses" }]
    };
  }

  const emails = parseEmailsFromText(trimmedText);

  if (emails.length === 0) {
    return {
      valid: [],
      errors: [{ type: "format", message: "No valid email addresses found. Ensure emails are comma-separated." }]
    };
  }

  const categorizedEmails = categorizeEmails(emails, existingEmails);
  const errors = buildErrorMessages(categorizedEmails);

  return { valid: categorizedEmails.valid, errors };
};

export const shouldBlockSubmission = (errors: ValidationError[]): boolean => {
  return errors.some(error => error.type !== "reactivate");
};
